import React, { useEffect, useState, useContext, createRef } from 'react';

import { Translate } from '@hydra/common/components';
import GenerationContext from '@hydra/common/components/GenerationContext';
import GenBar from '@hydra/common/components/GenBar';

import { removeUrlPath } from '@hydra/utils/urlUtil';

import { useI18n } from '@hydra/common/customHooks';

interface GenerationFormProps {
  className?: string,
  placeholder?: string,
  buttonText?: string,
  allowSubFolder?: string,
  subFolderPlaceholder?: string,
  homepageUrl?: string,
  handleSubmit: () => void
}

const GenerationForm = (props: GenerationFormProps) => {
  const [{ isLoading, isError, errorMessage, url }, dispatch] = useContext<any>(GenerationContext);
  const [error, setError] = useState(isError);
  const { locale, translate } = useI18n();
  const {
    className: classNameProp,
    placeholder,
    subFolderPlaceholder,
    buttonText,
    homepageUrl,
    allowSubFolder = false,
    handleSubmit
  } = props;
  const inputRef = createRef<any>();
  useEffect(() => {
    setError(isError);
    if (!allowSubFolder) {
      inputRef.current.focus();
    }
  }, [isError, inputRef, allowSubFolder]);

  const translatedError = (error: string) => {
    if (error === 'exists') {
      return <Translate content='generation_form_project_already_exists_with_link' with={{ url: removeUrlPath(url) }} />;
    }
    if (error === 'invalid') {
      return translate('generation_form_invalid_url');
    }
    if (error === 'no_internal_no_homepage_project') {
      const domain = removeUrlPath(url).split('/')[0];
      return <Translate content='generation_form_project_homepage' with={{ domain }} />;
    }
    if (error === 'unresolvable') {
      return translate('generation_form_unresolvable_url');
    }
    if (error === 'page_rate_limit') {
      return <Translate content='page_reviews_limit_reached' />;
    }
    if (error === 'no_internal_no_homepage_review') {
      const domain = removeUrlPath(url).split('/')[0];
      return (
        <Translate
          content='generation_form_no_internal_no_homepage_review'
          with={{ url: domain, lang: locale }}
        />
      );
    }
    return translate('error_oops');
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const cleanValue = value.split(' ').join('');
    dispatch({ type: 'ON_CHANGE', url: cleanValue });
  };

  const handleSubFolderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const target = e.target;
    const value = target.value;
    const cleanValue = value.split(' ').join('');
    dispatch({ type: 'ON_SUBFOLDER_CHANGE', subFolder: cleanValue });
  };

  const handleKeyDown = (e: any) => {
    switch (e.key) {
      case 'Escape':
        if (e.target.value !== '') {
          // Avoid Opera to exit fullscreen mode.
          e.preventDefault();
          // Avoid the Modal to handle the event.
          e.stopPropagation();
          handleClear();
        }
        break;
      case 'Enter':
        if (e.target.value !== '') {
          handleSubmit();
        }
    }
  };

  const handleClear = () => {
    inputRef.current.value = '';
    dispatch({ type: 'ON_CHANGE', url: '' });
  };

  return (
    <GenBar
      ref={inputRef}
      isLoading={isLoading}
      disabled={isLoading}
      handleChange={handleChange}
      handleSubFolderChange={handleSubFolderChange}
      handleSubmit={handleSubmit}
      handleKeyDown={handleKeyDown}
      handleClear={handleClear}
      placeholder={placeholder || translate('website_improve')}
      subFolderPlaceholder={subFolderPlaceholder || translate('generation_subfolder_placeholder_text')}
      value={url}
      homepageUrl={homepageUrl}
      error={error}
      allowSubFolder={allowSubFolder}
      helperText={error && translatedError(errorMessage)}
      buttonText={buttonText}
      className={classNameProp}
      id='generation-bar'
    />
  );
};

export default GenerationForm;
